import { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';
import SecurityOutlinedIcon from '@material-ui/icons/SecurityOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import {
  createFrontendModule,
  AppRootElementBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  oauthRequestApiRef,
  useApi,
  type PendingOAuthRequest,
} from '@backstage/core-plugin-api';
import { BrandMark } from './nav/BrandMark';
import { lightTokens } from './theme/rafayTheme';
import { GOOGLE_G_ICON } from './SessionExpiredDialog';

// Replaces Backstage's built-in OAuthRequestDialog (core-components), which
// the "app" plugin mounts unconditionally at the app root (see
// @backstage/plugin-app's oauth-request-dialog app-root-element — disabled
// in app-config.yaml) — it pops up in stock MUI/Google styling whenever any
// API call needs a fresh Google OAuth scope, independent of and often at the
// same time as this app's own SessionExpiredDialog, so the two looked like
// two overlapping, differently-styled "login" cards. This wires up the same
// oauthRequestApiRef the default dialog used, just in Control Tower's own
// card styling, so re-authorizing still works but there's only ever one
// visual "card" the user sees.
const CustomOAuthRequestDialog = () => {
  const oauthRequestApi = useApi(oauthRequestApiRef);
  const [requests, setRequests] = useState<PendingOAuthRequest[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const subscription = oauthRequestApi.authRequest$().subscribe(setRequests);
    return () => subscription.unsubscribe();
  }, [oauthRequestApi]);

  const request = requests[0];

  const handleContinue = async () => {
    if (!request) return;
    setBusy(true);
    try {
      await request.trigger();
    } finally {
      setBusy(false);
    }
  };

  const handleRejectAll = () => {
    requests.forEach(r => r.reject());
  };

  return (
    <Dialog
      open={requests.length > 0}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 16 } }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        style={{ padding: '40px 40px 32px', textAlign: 'center' }}
      >
        <Box>
          <BrandMark size={40} withText />
        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={72}
          height={72}
          borderRadius="50%"
          style={{
            marginTop: 28,
            marginBottom: 20,
            background: lightTokens.tealSoftBg,
            color: lightTokens.teal,
          }}
        >
          <SecurityOutlinedIcon style={{ fontSize: 34 }} />
        </Box>

        <Typography
          variant="h6"
          style={{ fontWeight: 700, color: lightTokens.textPrimary }}
        >
          Additional authorization required
        </Typography>
        <Typography
          variant="body2"
          style={{ color: lightTokens.textSecondary, marginTop: 8 }}
        >
          {request?.provider.message ??
            `${
              request?.provider.title ?? 'Google'
            } needs a fresh permission grant to continue.`}
        </Typography>

        <Button
          variant="outlined"
          fullWidth
          disabled={busy || !request}
          onClick={handleContinue}
          style={{
            marginTop: 28,
            height: 50,
            borderRadius: 24,
            borderColor: lightTokens.border,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
          }}
          startIcon={
            busy ? (
              <CircularProgress size={16} thickness={5} />
            ) : (
              <img src={GOOGLE_G_ICON} alt="" width={20} height={20} />
            )
          }
        >
          Continue with {request?.provider.title ?? 'Google'}
        </Button>

        <Box
          display="flex"
          alignItems="center"
          width="100%"
          style={{ gap: 12, margin: '20px 0' }}
        >
          <Divider style={{ flex: 1 }} />
          <Typography variant="caption" color="textSecondary">
            or
          </Typography>
          <Divider style={{ flex: 1 }} />
        </Box>

        <Link
          component="button"
          variant="body2"
          onClick={handleRejectAll}
          style={{ color: lightTokens.textSecondary }}
        >
          Cancel
        </Link>
      </Box>

      <Divider />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{
          gap: 6,
          padding: '14px 0',
          fontSize: 11.5,
          color: lightTokens.textSecondary,
        }}
      >
        <LockOutlinedIcon style={{ fontSize: 13 }} />
        Secure authentication powered by RAFAY SSO
      </Box>
    </Dialog>
  );
};

export const oauthRequestDialogModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootElementBlueprint.make({
      name: 'custom-oauth-request-dialog',
      params: { element: <CustomOAuthRequestDialog /> },
    }),
  ],
});
